import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Form, Badge } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import toast from 'react-hot-toast';
import masterDataService from '../../../services/masterDataService';

/**
 * Master Data Records Modal
 * Displays records from a selected master data table with pagination and search
 */
const MasterDataRecordsModal = ({
  show,
  onHide,
  table
}) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [columns, setColumns] = useState([]);

  // Fetch records when modal opens or pagination changes
  useEffect(() => {
    if (show && table) {
      fetchRecords();
    }
  }, [show, table, page, pageSize]);

  const fetchRecords = async () => {
    if (!table || !table.table_id) return;

    setLoading(true);
    try {
      const response = await masterDataService.getTableRecords(
        table.table_id,
        { page, page_size: pageSize }
      );

      if (response && response.data) {
        const { records: fetchedRecords, pagination, columns: tableColumns } = response.data;

        setRecords(fetchedRecords || []);
        setTotalRecords(pagination?.total || fetchedRecords?.length || 0);

        // Build columns dynamically from first record or table schema
        if (fetchedRecords && fetchedRecords.length > 0) {
          buildColumns(fetchedRecords[0], tableColumns);
        } else if (tableColumns) {
          buildColumnsFromSchema(tableColumns);
        }
      }
    } catch (error) {
      console.error('Error fetching table records:', error);
      toast.error('Failed to load table records');
    } finally {
      setLoading(false);
    }
  };

  // Build columns from first record
  const buildColumns = (firstRecord, tableColumns) => {
    const cols = Object.keys(firstRecord)
      .filter(key => key !== 'id' && key !== '_id' && key !== 'record_id')
      .map(key => {
        const columnInfo = tableColumns?.find(col => col.column_id === key);

        return {
          name: columnInfo?.display_name || formatColumnName(key),
          selector: row => row[key],
          sortable: true,
          wrap: true,
          cell: row => (
            <div style={{ padding: '8px 0' }}>
              {row[key] !== null && row[key] !== undefined ? String(row[key]) : '-'}
            </div>
          )
        };
      });

    setColumns(cols);
  };

  // Build columns from schema
  const buildColumnsFromSchema = (tableColumns) => {
    const cols = tableColumns.map(col => ({
      name: col.display_name || col.column_id,
      selector: row => row[col.column_id],
      sortable: true,
      wrap: true
    }));

    setColumns(cols);
  };

  // Format column name for display
  const formatColumnName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPageSize, newPage) => {
    setPageSize(newPageSize);
    setPage(newPage);
  };

  // Filter records based on search
  const filteredRecords = records.filter(record => {
    if (!searchTerm) return true;

    return Object.values(record).some(value =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {table?.display_name || table?.table_id || 'Master Data Records'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Table Info */}
        <div className="mb-3">
          <Badge bg="primary" className="me-2">
            Table: {table?.table_id}
          </Badge>
          {table?.category && (
            <Badge bg="secondary" className="me-2">
              Category: {table.category}
            </Badge>
          )}
          <Badge bg="success">
            Total Records: {totalRecords}
          </Badge>
        </div>

        {/* Search */}
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Search in table..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Form.Group>

        {/* Data Table */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading records...</p>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center text-muted py-5">
            <p>No records found in this table</p>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={filteredRecords}
            pagination
            paginationServer={!searchTerm}
            paginationTotalRows={searchTerm ? filteredRecords.length : totalRecords}
            paginationDefaultPage={page}
            paginationPerPage={pageSize}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
            highlightOnHover
            striped
            responsive
            noDataComponent="No records match your search"
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MasterDataRecordsModal;
